/**
 * Shared text-glTF bundle reader and Unity-compatible packager.
 *
 * Unity imports a loose `.gltf` from a real filesystem, lets glTFast resolve
 * sibling buffers/images, then stores the manifest and those resources as
 * `{assetId}.gltfzip` (`GLTFastImporter` → `ProjectModel.CreatePackage` →
 * `ZipHelper.ZipFiles`). A browser receives only the files the user explicitly
 * selected, so the import surface passes a set of entries here instead.
 *
 * Both fresh imports and cloud reloads use this module. Keeping one resolver is
 * important: a bundle accepted before upload must not become unreadable after
 * the first page reload.
 */

import { LoadingManager } from "three";
import type { GLTF } from "three/addons/loaders/GLTFLoader.js";

import { createGltfLoader } from "./gltfLoaderFactory";
import {
  unzipBlobEntriesAsync,
  zipBlobEntriesAsync,
} from "./zipBlobStreams";

export interface GltfResourceBundle {
  /** Manifest path inside the selected directory/archive. */
  readonly rootPath: string;
  /** Every selected/archive entry, keyed by its relative path. */
  readonly entries: ReadonlyMap<string, Blob>;
}

export interface ParseGltfBundleOptions {
  readonly signal?: AbortSignal;
  /**
   * Cloud assets keep rendering when only a texture is absent, matching the
   * previous read path. New imports use `"error"` so they cannot persist a
   * package that is already incomplete.
   */
  readonly missingImages?: "error" | "placeholder";
}

export interface GltfBundleInspection {
  readonly resourceUris: ReadonlyArray<string>;
  readonly missingUris: ReadonlyArray<string>;
  readonly remoteUris: ReadonlyArray<string>;
}

export class GltfResourceBundleError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "GltfResourceBundleError";
  }
}

const K_GLTF_EXTENSIONS = new Set([".gltf", ".gltf2"]);

/** 1×1 transparent PNG — read-side fallback for a missing external texture. */
const K_TRANSPARENT_PNG =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==";

/**
 * Build a bundle from already-read entries. Paths may use either slash style;
 * duplicate normalized paths are rejected instead of silently choosing bytes.
 */
export function createGltfResourceBundle(
  entries: Iterable<readonly [string, Blob]>,
  requestedRootPath?: string,
): GltfResourceBundle {
  const normalized = new Map<string, Blob>();
  for (const [rawPath, bytes] of entries) {
    const slashPath = rawPath.replace(/\\/g, "/");
    if (slashPath.endsWith("/")) continue;
    if (slashPath.startsWith("/") || slashPath.split("/").includes("..")) {
      throw new GltfResourceBundleError(
        `The glTF bundle entry "${rawPath}" is not a safe relative path.`,
      );
    }
    const path = normalizeBundlePath(rawPath);
    if (!path) continue;
    if (normalized.has(path)) {
      throw new GltfResourceBundleError(
        `The glTF bundle contains more than one entry named "${path}".`,
      );
    }
    normalized.set(path, bytes);
  }

  const rootPath = requestedRootPath
    ? resolveRequestedRoot(normalized, requestedRootPath)
    : findRootPath(normalized);
  if (!rootPath) {
    throw new GltfResourceBundleError(
      "No .gltf manifest was found. Select the .gltf file together with its .bin and texture files.",
    );
  }
  return { rootPath, entries: normalized };
}

/** Read a `.gltfzip` Blob without blocking the render thread on decompression. */
export async function readGltfZipBundleAsync(
  blob: Blob,
  signal?: AbortSignal,
): Promise<GltfResourceBundle> {
  throwIfAborted(signal);
  let entries: Map<string, Blob>;
  try {
    entries = await unzipBlobEntriesAsync(blob, signal);
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new GltfResourceBundleError("The .gltfzip archive could not be opened.", err);
  }
  return createGltfResourceBundle(entries);
}

/**
 * Inspect standard glTF external-resource references (`buffers[].uri` and
 * `images[].uri`) against the bundle. Embedded data/buffer-view images need no
 * companion file. Remote URLs are reported separately because Unity's package
 * contract is self-contained and cannot preserve them.
 */
export async function inspectGltfResourceBundle(
  bundle: GltfResourceBundle,
): Promise<GltfBundleInspection> {
  const resourceUris = await readExternalResourceUris(bundle);
  const missingUris: string[] = [];
  const remoteUris: string[] = [];
  for (const uri of resourceUris) {
    if (isEmbeddedUri(uri)) continue;
    if (isRemoteUri(uri)) {
      remoteUris.push(uri);
      continue;
    }
    if (!resolveResourceEntry(bundle, uri)) missingUris.push(uri);
  }
  return { resourceUris, missingUris, remoteUris };
}

/**
 * Parse a text-glTF bundle through the same URL resolver used for cloud reload.
 * Every temporary object URL is revoked once GLTFLoader has consumed it.
 */
export async function parseGltfResourceBundleAsync(
  bundle: GltfResourceBundle,
  options: ParseGltfBundleOptions = {},
): Promise<GLTF> {
  const { signal, missingImages = "error" } = options;
  throwIfAborted(signal);
  const inspection = await inspectGltfResourceBundle(bundle);
  if (inspection.remoteUris.length > 0) {
    throw new GltfResourceBundleError(
      `Remote glTF resources are not supported: ${quotedList(inspection.remoteUris)}.`,
    );
  }

  const missingRequired = inspection.missingUris.filter(
    (uri) => missingImages === "error" || !isImageUri(uri),
  );
  if (missingRequired.length > 0) {
    throw new GltfResourceBundleError(
      `Missing glTF companion ${missingRequired.length === 1 ? "file" : "files"}: `
      + `${quotedList(missingRequired)}. Select the .gltf, .bin, and textures together.`,
    );
  }

  const resourceUrls = new Map<string, string>();
  const localUrls: string[] = [];
  for (const uri of inspection.resourceUris) {
    if (isEmbeddedUri(uri) || isRemoteUri(uri)) continue;
    const resolved = resolveResourceEntry(bundle, uri);
    if (!resolved) continue;
    if (resourceUrls.has(uri)) continue;
    const resourceBlob = resolved.blob.type
      ? resolved.blob
      : resolved.blob.slice(0, resolved.blob.size, mimeForPath(resolved.path));
    const url = URL.createObjectURL(resourceBlob);
    localUrls.push(url);
    resourceUrls.set(uri, url);
    // LoadingManager has historically received both raw and decoded URI
    // spellings across Three releases; index both directions.
    resourceUrls.set(safeDecodeUriComponent(uri), url);
    resourceUrls.set(safeEncodeUriPath(uri), url);
  }

  const manager = new LoadingManager();
  manager.setURLModifier((url) => {
    if (isEmbeddedUri(url)) return url;
    const direct =
      resourceUrls.get(url)
      ?? resourceUrls.get(safeDecodeUriComponent(url))
      ?? resourceUrls.get(safeEncodeUriPath(url));
    if (direct) return direct;
    if (missingImages === "placeholder" && isImageUri(url)) {
      console.warn(
        `[GltfResourceBundle] missing texture "${url}" — using transparent placeholder`,
      );
      return K_TRANSPARENT_PNG;
    }
    return url;
  });

  try {
    const root = bundle.entries.get(bundle.rootPath);
    if (!root) {
      throw new GltfResourceBundleError(
        `The glTF manifest "${bundle.rootPath}" is missing from the bundle.`,
      );
    }
    const gltf = await createGltfLoader(manager).parseAsync(await root.arrayBuffer(), "");
    throwIfAborted(signal);
    return gltf;
  } catch (err) {
    if (isAbortError(err) || err instanceof GltfResourceBundleError) throw err;
    throw new GltfResourceBundleError("The glTF manifest or one of its resources is invalid.", err);
  } finally {
    for (const url of localUrls) URL.revokeObjectURL(url);
  }
}

/**
 * Repackage a valid resource set as the same self-contained `.gltfzip` object
 * Unity uploads for a loose `.gltf`. The manifest is always top-level and uses
 * a `.gltf` suffix so Unity's unzip reader can discover `.gltf2` inputs too.
 */
export async function createUnityGltfZipAsync(
  bundle: GltfResourceBundle,
  signal?: AbortSignal,
): Promise<Blob> {
  throwIfAborted(signal);
  const inspection = await inspectGltfResourceBundle(bundle);
  if (inspection.remoteUris.length > 0) {
    throw new GltfResourceBundleError(
      `Remote glTF resources are not supported: ${quotedList(inspection.remoteUris)}.`,
    );
  }
  if (inspection.missingUris.length > 0) {
    throw new GltfResourceBundleError(
      `Missing glTF companion ${inspection.missingUris.length === 1 ? "file" : "files"}: `
      + `${quotedList(inspection.missingUris)}. Select the .gltf, .bin, and textures together.`,
    );
  }

  const rootBytes = bundle.entries.get(bundle.rootPath);
  if (!rootBytes) {
    throw new GltfResourceBundleError(
      `The glTF manifest "${bundle.rootPath}" is missing from the bundle.`,
    );
  }

  const archive: Array<readonly [string, Blob]> = [
    [topLevelGltfName(bundle.rootPath), rootBytes],
  ];
  const archivePaths = new Set(archive.map(([path]) => path));
  for (const uri of inspection.resourceUris) {
    if (isEmbeddedUri(uri) || isRemoteUri(uri)) continue;
    const resolved = resolveResourceEntry(bundle, uri);
    if (!resolved) continue; // guarded above; keeps narrowing local
    const entryPath = canonicalResourcePath(uri);
    if (archivePaths.has(entryPath)) {
      throw new GltfResourceBundleError(
        `More than one glTF resource resolves to "${entryPath}".`,
      );
    }
    archivePaths.add(entryPath);
    archive.push([entryPath, resolved.blob]);
  }

  return await zipBlobEntriesAsync(archive, signal);
}

/** Normalize selected-file/archive paths without touching percent escapes. */
export function normalizeBundlePath(path: string): string {
  const slashes = path.replace(/\\/g, "/");
  const parts: string[] = [];
  for (const part of slashes.split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") {
      if (parts.length > 0) parts.pop();
      continue;
    }
    parts.push(part);
  }
  return parts.join("/");
}

function resolveRequestedRoot(
  entries: ReadonlyMap<string, Blob>,
  requested: string,
): string | null {
  const normalized = normalizeBundlePath(requested);
  if (entries.has(normalized) && K_GLTF_EXTENSIONS.has(extensionOf(normalized))) {
    return normalized;
  }
  const decoded = safeDecodeUriComponent(normalized);
  for (const path of entries.keys()) {
    if (
      K_GLTF_EXTENSIONS.has(extensionOf(path))
      && safeDecodeUriComponent(path) === decoded
    ) {
      return path;
    }
  }
  return null;
}

function findRootPath(entries: ReadonlyMap<string, Blob>): string | null {
  const candidates = [...entries.keys()].filter((path) =>
    K_GLTF_EXTENSIONS.has(extensionOf(path)),
  );
  if (candidates.length === 0) return null;
  const topLevel = candidates.filter((path) => !path.includes("/"));
  if (topLevel.length === 1) return topLevel[0];
  if (topLevel.length > 1) {
    throw new GltfResourceBundleError(
      `The glTF bundle contains multiple top-level manifests: ${quotedList(topLevel)}.`,
    );
  }
  if (candidates.length === 1) return candidates[0];
  throw new GltfResourceBundleError(
    `The glTF bundle contains multiple manifests: ${quotedList(candidates)}.`,
  );
}

async function readExternalResourceUris(bundle: GltfResourceBundle): Promise<string[]> {
  const root = bundle.entries.get(bundle.rootPath);
  if (!root) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse((await root.text()).replace(/^\uFEFF/, ""));
  } catch (err) {
    throw new GltfResourceBundleError(
      `The glTF manifest "${bundle.rootPath}" is not valid JSON.`,
      err,
    );
  }
  if (!isRecord(parsed)) {
    throw new GltfResourceBundleError(
      `The glTF manifest "${bundle.rootPath}" must contain a JSON object.`,
    );
  }

  const result: string[] = [];
  collectUris(parsed.buffers, result);
  collectUris(parsed.images, result);
  return [...new Set(result)];
}

function collectUris(value: unknown, target: string[]): void {
  if (!Array.isArray(value)) return;
  for (const item of value) {
    if (!isRecord(item) || typeof item.uri !== "string" || !item.uri) continue;
    target.push(item.uri);
  }
}

function resolveResourceEntry(
  bundle: GltfResourceBundle,
  uri: string,
): { readonly path: string; readonly blob: Blob } | null {
  const uriPath = stripUriSuffix(uri);
  const rootDir = directoryOf(bundle.rootPath);
  const relativeCandidates = uniqueStrings([
    joinBundlePath(rootDir, uriPath),
    joinBundlePath(rootDir, safeDecodeUriComponent(uriPath)),
    joinBundlePath(rootDir, safeEncodeUriPath(uriPath)),
    normalizeBundlePath(uriPath),
    normalizeBundlePath(safeDecodeUriComponent(uriPath)),
    normalizeBundlePath(safeEncodeUriPath(uriPath)),
  ]);

  for (const candidate of relativeCandidates) {
    const direct = bundle.entries.get(candidate);
    if (direct && direct.size > 0) return { path: candidate, blob: direct };
  }

  // A normal multiple-file picker exposes only basenames. Accept that shape
  // only when it is unambiguous; directory selection preserves full paths.
  const wantedBase = safeDecodeUriComponent(baseNameOf(uriPath)).toLowerCase();
  const basenameMatches = [...bundle.entries.entries()].filter(([path]) =>
    safeDecodeUriComponent(baseNameOf(path)).toLowerCase() === wantedBase,
  ).filter(([, blob]) =>
    blob.size > 0,
  );
  if (basenameMatches.length === 1) {
    return { path: basenameMatches[0][0], blob: basenameMatches[0][1] };
  }
  if (basenameMatches.length > 1) {
    throw new GltfResourceBundleError(
      `The glTF resource "${uri}" is ambiguous. Import a .gltfzip so relative paths are preserved.`,
    );
  }
  return null;
}

function canonicalResourcePath(uri: string): string {
  const raw = stripUriSuffix(uri).replace(/\\/g, "/");
  const decoded = safeDecodeUriComponent(raw);
  const unsafe =
    raw.startsWith("/")
    || decoded.startsWith("/")
    || raw.split("/").includes("..")
    || decoded.split("/").includes("..");
  const normalized = normalizeBundlePath(raw);
  if (unsafe || !normalized) {
    throw new GltfResourceBundleError(`The glTF resource path "${uri}" is not safe to package.`);
  }
  // Unity's `ZipHelper` uses the URI string from the glTF JSON verbatim as
  // the ZipEntry name. Keep `%20` (and every other escape) intact here; the
  // reader accepts both raw and decoded spellings for historical packages.
  return normalized;
}

function topLevelGltfName(rootPath: string): string {
  const base = baseNameOf(rootPath);
  const ext = extensionOf(base);
  return ext === ".gltf" ? base : `${base.slice(0, -ext.length)}.gltf`;
}

function directoryOf(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash < 0 ? "" : path.slice(0, slash);
}

function baseNameOf(path: string): string {
  const normalized = normalizeBundlePath(path);
  const slash = normalized.lastIndexOf("/");
  return slash < 0 ? normalized : normalized.slice(slash + 1);
}

function joinBundlePath(base: string, relative: string): string {
  return normalizeBundlePath(base ? `${base}/${relative}` : relative);
}

function extensionOf(path: string): string {
  const clean = stripUriSuffix(path);
  const dot = clean.lastIndexOf(".");
  return dot < 0 ? "" : clean.slice(dot).toLowerCase();
}

function stripUriSuffix(uri: string): string {
  const query = uri.indexOf("?");
  const fragment = uri.indexOf("#");
  const cut =
    query < 0 ? fragment
      : fragment < 0 ? query
        : Math.min(query, fragment);
  return cut < 0 ? uri : uri.slice(0, cut);
}

function isImageUri(path: string): boolean {
  return [".png", ".jpg", ".jpeg", ".webp", ".ktx2", ".gif", ".bmp"].includes(
    extensionOf(path),
  );
}

function isEmbeddedUri(uri: string): boolean {
  return uri.startsWith("data:") || uri.startsWith("blob:");
}

function isRemoteUri(uri: string): boolean {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/|\/)/i.test(uri);
}

function mimeForPath(path: string): string {
  switch (extensionOf(path)) {
    case ".png": return "image/png";
    case ".jpg":
    case ".jpeg": return "image/jpeg";
    case ".webp": return "image/webp";
    case ".ktx2": return "image/ktx2";
    default: return "application/octet-stream";
  }
}

function safeDecodeUriComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function safeEncodeUriPath(value: string): string {
  return value
    .split("/")
    .map((part) => {
      try {
        return encodeURIComponent(decodeURIComponent(part));
      } catch {
        return encodeURIComponent(part);
      }
    })
    .join("/");
}

function quotedList(values: ReadonlyArray<string>): string {
  return values.map((value) => `"${safeDecodeUriComponent(value)}"`).join(", ");
}

function uniqueStrings(values: ReadonlyArray<string>): string[] {
  return [...new Set(values.filter(Boolean))];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw signal.reason instanceof Error
      ? signal.reason
      : new DOMException("Import cancelled.", "AbortError");
  }
}

function isAbortError(err: unknown): boolean {
  return (err instanceof DOMException || err instanceof Error) && err.name === "AbortError";
}
